/**
 * The picker vocabulary for the structured clause builder — everything a
 * dropdown can offer, derived from the live compile (all reuse, no new
 * derivations): type names off the resolved registry, element paths off the
 * wire indexes, aspect keys/values off the shared aspect helpers, planes off
 * the plane-switcher derivation.
 */
import { useCallback, useMemo } from "react";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useModelIndexes } from "../hooks/useModelIndexes.js";
import { aspectKeysOf, aspectLegend } from "../aspectColors.js";
import { computePlaneInfo } from "../planeFilter.js";
import { processQualifiedName } from "../processKey.js";
import { BUILTIN_KIND_ATOMS } from "./clauseModel.js";
import { collectViewGovernance } from "./viewPlan.js";
import { ICONS } from "../generated/stdlib-widgets/arch.ui/icons.js";

/** The enumerable stdlib widget icon set (identifiers `widget.icon:` accepts). */
const ICON_NAMES: readonly string[] = Object.keys(ICONS).sort();

export interface SelectorVocabulary {
  /** Type atoms: user types + the built-in element kinds. */
  readonly types: readonly string[];
  /** Element atoms: qualified module paths. */
  readonly elements: readonly string[];
  /** Aspect keys (string-valued classifications). */
  readonly aspectKeys: readonly string[];
  /** WRITTEN value forms for one aspect key (quoted classifications). */
  aspectValues(key: string): readonly string[];
  /** Plane names for `on`. */
  readonly planes: readonly string[];
  /** Qualified process names for a `flow` clause ref. */
  readonly processes: readonly string[];
  /** Stdlib widget icon identifiers for a `widget.icon:` style leaf. */
  readonly icons: readonly string[];
  /** Policy names in scope — the `violating <P>` style target's dropdown. Same
   *  governance closure the view planner resolves `violating` against. */
  readonly policies: readonly string[];
  /** Timeline markers the model writes, in axis order — the `at <point>`
   *  picker's stops (`now` is offered unconditionally, it is not a marker). */
  readonly markers: readonly string[];
}

export function useSelectorVocabulary(): SelectorVocabulary {
  const { model, resolvedModel, compiledSources } = useWorkspaceModel();
  const indexes = useModelIndexes(model);

  const types = useMemo(() => {
    const user = (resolvedModel?.types ?? [])
      .map((t) => t.name)
      .filter((n): n is string => typeof n === "string" && n.length > 0);
    return [...new Set([...user, ...BUILTIN_KIND_ATOMS])].sort();
  }, [resolvedModel]);

  const elements = useMemo(
    () => [...(indexes?.moduleQualifiedNameById.values() ?? [])].sort(),
    [indexes],
  );

  const aspectKeys = useMemo(() => (model ? aspectKeysOf(model.modules) : []), [model]);

  const aspectValues = useCallback(
    (key: string): readonly string[] =>
      model ? aspectLegend(model.modules, key).map((e) => `"${e.value}"`) : [],
    [model],
  );

  const planes = useMemo(
    () => (model ? computePlaneInfo(model.modules).planes : []),
    [model],
  );

  const processes = useMemo(
    () => [...(indexes?.processById.values() ?? [])].map(processQualifiedName).sort(),
    [indexes],
  );

  const markers = useMemo(() => resolvedModel?.timeline?.markers ?? [], [resolvedModel]);

  const policies = useMemo(
    () =>
      [...new Set((collectViewGovernance(resolvedModel, compiledSources)?.policyList ?? []).map((p) => p.name))].sort(),
    [resolvedModel, compiledSources],
  );

  return useMemo(
    () => ({ types, elements, aspectKeys, aspectValues, planes, processes, icons: ICON_NAMES, policies, markers }),
    [types, elements, aspectKeys, aspectValues, planes, processes, policies, markers],
  );
}